import {
  type Editor,
  type JSONContent,
  type PasteRuleFinder,
  type ExtendedRegExpMatchArray,
  callOrReturn,
  PasteRule,
} from "@tiptap/core";

import type { Node as PMNode } from "@tiptap/pm/model";
import { vdom, type VNode, type VElement } from "zeed-dom";
import { type SvelteComponent, getAllContexts } from "svelte";

export const deserialiseCRDT = (crdt?: string) => {
  if (!crdt) return new Uint8Array();
  return new Uint8Array(
    atob(crdt)
      .split(",")
      .map((c) => Number(c))
  );
};

// @ts-ignore - it works with strings
export const serialiseCRDT = (crdt: string | Uint8Array) => btoa(crdt);

// editor.isEmpty is no use as it does not collapse whitespace/newline elements
export const isBlank = (editor: Editor) => editor.state.doc.textContent.length === 0;

export const getContent = (
  json: JSONContent
): {
  empty: boolean;
  json: JSONContent;
  title: string;
  hasContentsBlock: boolean;
} => {
  let jsonContent: JSONContent[] = json.content || [];
  let hasContentsBlock = false;
  // drop inside of contents block if it exists
  if (jsonContent.length && jsonContent[0].type === "tableOfContents") {
    jsonContent = jsonContent[0].content || [];
    hasContentsBlock = true;
  }

  let valid = false;
  let title: string = "";
  // check for content and title
  for (const node of jsonContent) {
    // ignore rules
    if (node.type === "horizontalRule") {
      continue;
    }
    // only consider text nodes if they aren't blank
    if ((node.type === "paragraph" || node.type === "heading") && !node.content) {
      continue;
    }

    // store top-most title
    if (node.type === "title" && node.content?.length && !title) {
      title = node.content[0].text || "";
    }

    valid = true;
  }
  return {
    empty: !valid,
    json: valid ? json : {},
    title,
    hasContentsBlock,
  };
};

export const getEditorContent = (editor: Editor) => ({
  ...getContent(editor.getJSON()),
  text: editor.getText(),
  editor: editor,
});

export function NodePasteRule(config: {
  find: PasteRuleFinder;
  type: any; // NodeType
  rule?: "replace" | "append";
  getAttributes?:
    | Record<string, any>
    | ((match: ExtendedRegExpMatchArray) => Record<string, any>)
    | false
    | null;
}) {
  // replace is the default rule
  if (!config.rule) {
    config.rule = "replace";
  }

  return new PasteRule({
    find: config.find,
    handler: ({ state, range, match }) => {
      const attributes = callOrReturn(config.getAttributes, undefined, match);
      const { tr } = state;
      if (attributes === false || attributes === null) {
        return null;
      }

      if (config.rule === "replace") {
        tr.replaceWith(range.from, range.to, config.type.create(attributes));
      }
      if (config.rule === "append") {
        tr.insert(range.to, config.type.create(attributes));
      }
    },
  });
}

/** find a node in the dom with attr data-node-view-content DFS */
const findDOMNode = (node: VNode): VElement | null => {
  const elem = node as VElement;
  if (elem._attributes && "data-node-view-content" in elem._attributes) {
    return elem;
  }
  if (elem.children) {
    for (const child of elem.children) {
      const result = findDOMNode(child);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

const dummy = (name: string) => () => console.warn(name, "not available in SSR Renderer");

/** Create a HTML node for a svelte component in SSR.
 *
 *
 * @param tag - tag name of the component
 * @param Component - the svelte component
 * @param node - the PMNode for the component
 * @param hasContent - does the component allow content inside?
 * @param nestedContent - for explicitly declaring the content to be passed in
 *
 *   */
export const SvelteSSRRenderer = (
  tag: string,
  Component: SvelteComponent | any,
  {
    node,
    hasContent,
    nestedContent,
    attributes,
  }: { node: PMNode; hasContent?: boolean; nestedContent?: any[]; attributes: Record<string, any> }
) => {
  // svelte components in ssr have a render method
  if (Component.render) {
    // pass global context & dummy on drag func to component
    const context = new Map([...getAllContexts(), ["TipTapNodeView", { onDragStart: () => null }]]);
    // create component with default/dummy props to make component functional/renderable
    const { html } = Component.render(
      {
        node,
        isEditable: false,
        editor: null,
        deleteNode: dummy(`${tag}: deleteNode`),
        updateAttributes: dummy(`${tag}: updateAttributes`),
      },
      { context }
    );
    // recreate dom elements from svelte SSR HTML
    const dom = vdom(html);
    if (hasContent) {
      // find content element if component allows content inside
      const contentDOM = findDOMNode(dom);
      if (contentDOM) {
        return { dom, contentDOM } as any as {
          dom: Node;
          contentDOM: HTMLElement;
        };
      } else {
        console.warn(
          `SvelteSSRRenderer: Couldn't find NodeViewContent node - please make sure ${tag} has one`
        );
      }
    }
    // return SSR component as a dom node
    return dom as any as Node;
  }

  // if not in ssr, return default tag node (doesn't render content) - used for copy/paste
  // e.g., ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)]
  // add slot for children if component allows content inside, prioritizing nested content if provided
  let children;
  if (nestedContent) {
    children = nestedContent;
  } else {
    children = hasContent ? [0] : [];
  }
  return [tag, attributes, ...children] as any;
};

/**
 * NB: This uses the decimal base to convert from bytes to MB
 *
 * @param sizeInBytes - the size of the file in bytes
 * @return {number} the size of the file in MB
 */
export const getSizeInMb = (sizeInBytes: number): number => {
  return sizeInBytes / 1000000;
};

export const getSizeHuman = (sizeInBytes: number): string => {
  const kb = sizeInBytes / 1000;
  if (kb < 1000) {
    return Math.floor(kb) + " KB";
  } else {
    return Math.floor(kb / 1000) + " MB";
  }
};
